import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { seriesPalette } from '../theme.ts';
import {
  bodyTooltip,
  chartTitle,
  multiSeriesLegend,
  cartesianGrid,
  type OptionContext,
} from './shared.ts';

export function buildLineOption(
  config: Extract<ChartConfig, { type: 'line' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({ trigger: 'axis' }),
    legend: multiSeriesLegend(config.series.length, textColor),
    grid: cartesianGrid(config.title, config.series.length),
    xAxis: {
      type: 'category',
      data: config.labels,
      axisLabel: { color: textColor, fontSize: 10 },
      axisLine: { lineStyle: { color: axisLineColor } },
      boundaryGap: false,
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: textColor, fontSize: 10 },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    color: seriesPalette(colors),
    series: config.series.map((s) => ({
      name: s.name,
      type: 'line' as const,
      data: s.values,
      smooth: true,
      symbol: 'circle',
      symbolSize: 5,
      lineStyle: { width: 2 },
    })),
  };
}

export function buildAreaOption(
  config: Extract<ChartConfig, { type: 'area' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const palette = seriesPalette(colors);
  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({ trigger: 'axis' }),
    legend: multiSeriesLegend(config.series.length, textColor),
    grid: cartesianGrid(config.title, config.series.length),
    xAxis: {
      type: 'category',
      data: config.labels,
      axisLabel: { color: textColor, fontSize: 10 },
      axisLine: { lineStyle: { color: axisLineColor } },
      boundaryGap: false,
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: textColor, fontSize: 10 },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    color: palette,
    series: config.series.map((s, si) => {
      const c = palette[si % palette.length] ?? colors.primary;
      return {
        name: s.name,
        type: 'line' as const,
        data: s.values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, color: c },
        areaStyle: { color: c, opacity: 0.2 },
      };
    }),
  };
}
